import { Button, Icon } from "@components/library";
import RefineRecommendations from "@components/RequestPageComponents/RefineRecommendations";
import RequestPageFinalistCount from "@components/RequestPageComponents/RequestPageFinalistCount";
import { COLORS, FEATURE_FLAGS, FONTS, STYLES, WINDOW_DIMENSIONS } from "@constants";
import useFollowRequest from "@hooks/useFollowRequest";
import { toggleLoginModal } from "@redux/actions/profileActions";
import { RootState } from "@redux/store";
import { getDraftRfsProposal } from "@requests/requestForStartupProposals";
import { useQuery } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { RequestForStartupsShowResponse } from "@tsTypes/request_for_startups/show";
import { LaunchableRequestStatus } from "@tsTypes/requests";
import { UserRole } from "@tsTypes/users";
import { track } from "@utils/appUtils";
import { setPrevUrlCookie } from "@utils/authUtils";
import { t } from "@utils/i18n";
import { getLaunchableRequestStatus, requestProposalUrlPath } from "@utils/requestUtils";
import { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Status from "./Status";

interface Props {
  request: RequestForStartupsShowResponse;
  onShare: () => void;
}

export default function Cta({ request, onShare }: Props) {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const history = useHistory();
  const dispatch = useDispatch();

  const { data: draft } = useQuery({
    queryKey: ["requestForStartupDraft", request.slug],
    queryFn: () => getDraftRfsProposal(request.slug),
    enabled: Boolean(request.slug) && currentUser.role === UserRole.SCIENTIST,
  });
  const hasDraft = Boolean(draft);

  const status = getLaunchableRequestStatus(request as any);
  const canApply =
    currentUser.role !== UserRole.UNIVERSITY_ADMIN &&
    (status === LaunchableRequestStatus.ACTIVE || status === LaunchableRequestStatus.IN_EXTENSION);

  const handleSubmitClick = () => {
    track(SegmentEventName.CLICK, {
      ui_component: "Request for Startup Page - Submit internal button",
      request_slug: request.slug,
    });

    const submitUrl = requestProposalUrlPath(request);

    if (currentUser.id) {
      history.push(submitUrl);
    } else {
      setPrevUrlCookie(submitUrl);

      dispatch(toggleLoginModal({ open: true }));
    }
  };

  const handleShareClick = () => {
    track(SegmentEventName.CLICK, {
      ui_component: "Request for Startup Page - Share button",
      request_slug: request.slug,
    });
    if (currentUser.id) {
      onShare();
    } else {
      setPrevUrlCookie(window.location.pathname);
      dispatch(toggleLoginModal({ open: true }));
    }
  };

  const { handleFollowRequest, isUserFollowing } = useFollowRequest(request.id);

  const handleNotify = () => {
    track(SegmentEventName.CLICK, {
      ui_component: "Request for Startup Page - Notify button",
      request_slug: request.slug,
    });

    handleFollowRequest();
  };

  let text = t("pages.request_for_startups_page.cta.text.share");
  const buttons: ReactNode[] = [
    <Button
      key="cta-share-button"
      leftIconName="Share"
      width="100%"
      onClick={handleShareClick}
      variant={
        (canApply ||
          status === LaunchableRequestStatus.COMING_SOON ||
          status === LaunchableRequestStatus.CLOSED) &&
        "secondary"
      }
    >
      {t("pages.request_for_startups_page.cta.button.share")}
    </Button>,
  ];

  if (status === LaunchableRequestStatus.COMING_SOON || status === LaunchableRequestStatus.CLOSED) {
    text = t("pages.request_for_startups_page.cta.text.coming_soon");
    buttons.unshift(
      isUserFollowing ? (
        <FollowingConfirmation>
          <Icon color={COLORS.GREEN} margin="0 4px 0 0" name="Success" />
          You&apos;ll be notified
        </FollowingConfirmation>
      ) : (
        <Button
          key="cta-notify-button"
          leftIconName="Bell"
          margin="0 0 10px"
          width="100%"
          onClick={handleNotify}
        >
          {t("pages.request_for_startups_page.cta.button.coming_soon")}
        </Button>
      )
    );
  } else if (request.requestable.external_url) {
    text = t("pages.request_for_startups_page.cta.text.external");
    buttons.unshift(
      <a href={request.requestable.external_url} target="_blank" rel="noreferrer">
        <Button
          key="cta-submit-button"
          rightIconName="Open in a new tab"
          margin="0 0 10px"
          width="100%"
          onClick={() => {
            track(SegmentEventName.CLICK, {
              ui_component: "Request for Startup Page - Submit external button",
              request_slug: request.slug,
            });
          }}
        >
          {t("pages.request_for_startups_page.cta.button.external")}
        </Button>
      </a>
    );
  } else if (canApply) {
    text = t("pages.request_for_startups_page.cta.text.submit");
    buttons.unshift(
      <Button
        key="cta-submit-button"
        leftIconName={hasDraft ? "Attention" : "Easy Apply"}
        margin="0 0 10px"
        width="100%"
        onClick={handleSubmitClick}
        disabled={currentUser.role === UserRole.SPONSOR}
      >
        {t(["pages.request_for_startups_page.cta.button", hasDraft ? "continue" : "submit"])}
      </Button>
    );
  }

  return (
    <Container>
      <TopContainer $showBorder={FEATURE_FLAGS.RELEVANT_NOT_RELEVANT_BUTTONS}>
        {text}
        <StatusContainer>
          {FEATURE_FLAGS.ARCHIVE_REQUESTS && request.archived_at && request.finalist_count > 0 ? (
            <RequestPageFinalistCount count={request.finalist_count} />
          ) : (
            <Status request={request as any} iconPosition="left" />
          )}
        </StatusContainer>
        {buttons}
      </TopContainer>
      {FEATURE_FLAGS.RELEVANT_NOT_RELEVANT_BUTTONS && (
        <RefineRecommendations
          request={request}
          isComplete={status === LaunchableRequestStatus.CLOSED}
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  min-width: 280px;
  width: 100%;

  border-radius: 8px;
  border: 1px solid ${COLORS.NEUTRAL_250};
  box-shadow: ${STYLES.SHADOW_B};

  ${FONTS.SEMIBOLD_1};

  ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
    margin-top: 40px;
  }
`;

const TopContainer = styled.div<{ $showBorder: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  padding: 24px 24px 32px;

  ${({ $showBorder }) => $showBorder && `border-bottom: 1px solid ${COLORS.NEUTRAL_250};`}
`;

const StatusContainer = styled.div`
  margin: 8px 0 16px;
`;
const FollowingConfirmation = styled.div`
  display: flex;
  align-items: center;
  height: 42px;
  color: ${COLORS.GREEN};
  ${FONTS.MEDIUM_2};
  margin: 0 auto 10px;
`;
